import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listBoards, getBoard, saveBoard, deleteBoard } from "./api";
import { fileToResizedDataUrl } from "./imageUtils";
import "./jeopardy.css";

const ROUND_TEMPLATES = [
    { name: "Jeopardy!", values: [200, 400, 600, 800, 1000] },
    { name: "Double Jeopardy!", values: [400, 800, 1200, 1600, 2000] },
];

function blankClue(value) {
    return { value, type: "text", prompt: "", answer: "" };
}

const CATEGORY_COUNT = 6;

function blankRounds() {
    return ROUND_TEMPLATES.map((template) => ({
        name: template.name,
        categories: Array.from({ length: CATEGORY_COUNT }, () => ""),
        clues: Array.from({ length: CATEGORY_COUNT }, () => template.values.map((v) => blankClue(v))),
    }));
}

export default function BoardBuilder() {
    const [rounds, setRounds] = useState(blankRounds);
    const [activeRound, setActiveRound] = useState(0);
    const [selected, setSelected] = useState(null);
    const [name, setName] = useState("");
    const [boardId, setBoardId] = useState(null);
    const [savedList, setSavedList] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        refreshList();
    }, []);

    async function refreshList() {
        try {
            const res = await listBoards();
            setSavedList(res.boards || []);
        } catch {
            // Non-critical - the load list just won't show up.
        }
        setLoadingList(false);
    }

    async function handleLoad(id) {
        setStatus(null);
        try {
            const res = await getBoard(id);
            if (res.error) throw new Error(res.error);
            setRounds(res.rounds);
            setName(res.name);
            setBoardId(res.id);
            setActiveRound(0);
            setSelected(null);
        } catch {
            setStatus({ type: "error", text: "Could not load that game." });
        }
    }

    function handleNew() {
        setRounds(blankRounds());
        setName("");
        setBoardId(null);
        setActiveRound(0);
        setSelected(null);
        setStatus(null);
    }

    function updateCategory(roundIdx, catIdx, value) {
        setRounds((prev) =>
            prev.map((r, ri) => {
                if (ri !== roundIdx) return r;
                const categories = [...r.categories];
                categories[catIdx] = value;
                return { ...r, categories };
            })
        );
    }

    function updateClue(roundIdx, catIdx, rowIdx, patch) {
        setRounds((prev) =>
            prev.map((r, ri) => {
                if (ri !== roundIdx) return r;
                const clues = r.clues.map((col, ci) => {
                    if (ci !== catIdx) return col;
                    return col.map((clue, ri2) => (ri2 === rowIdx ? { ...clue, ...patch } : clue));
                });
                return { ...r, clues };
            })
        );
    }

    async function handleImageUpload(roundIdx, catIdx, rowIdx, file) {
        if (!file) return;
        try {
            const dataUrl = await fileToResizedDataUrl(file);
            updateClue(roundIdx, catIdx, rowIdx, { type: "image", prompt: dataUrl });
        } catch {
            setStatus({ type: "error", text: "Could not process that image." });
        }
    }

    async function handleSave() {
        const trimmed = name.trim();
        if (!trimmed) {
            setStatus({ type: "error", text: "Please enter a name for this game." });
            return;
        }
        setSaving(true);
        setStatus(null);
        try {
            const res = await saveBoard(boardId, trimmed, rounds);
            if (res.error) throw new Error(res.error);
            setBoardId(res.id);
            setStatus({ type: "success", text: "Saved!" });
            refreshList();
        } catch {
            setStatus({ type: "error", text: "Could not save this game. It may be too large - try smaller images." });
        }
        setSaving(false);
    }

    async function handleDelete() {
        if (!boardId) return;
        if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
        try {
            await deleteBoard(boardId);
            handleNew();
            refreshList();
        } catch {
            setStatus({ type: "error", text: "Could not delete this game." });
        }
    }

    const round = rounds[activeRound];
    const selectedClue = selected ? round.clues[selected.category][selected.row] : null;

    return (
        <div className="jpd-home">
            <h1 className="jpd-title">Game Builder</h1>

            <div className="jpd-panel jpd-panel-wide">
                <div className="jpd-builder-toprow">
                    <input
                        className="jpd-input"
                        placeholder="Game name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={100}
                    />
                    <button className="jpd-btn jpd-btn-primary" onClick={handleSave} disabled={saving}>
                        {boardId ? "Update Game" : "Save Game"}
                    </button>
                    <button className="jpd-btn" onClick={handleNew}>
                        New Game
                    </button>
                    {boardId && (
                        <button className="jpd-btn jpd-btn-danger" onClick={handleDelete}>
                            Delete
                        </button>
                    )}
                </div>

                {status && <div className={status.type === "error" ? "jpd-error" : "jpd-success"}>{status.text}</div>}

                {!loadingList && savedList.length > 0 && (
                    <div className="jpd-builder-loadrow">
                        <span className="jpd-muted">Load existing:</span>
                        {savedList.map((b) => (
                            <button key={b.id} className="jpd-btn jpd-btn-small" onClick={() => handleLoad(b.id)}>
                                {b.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="jpd-panel jpd-panel-wide">
                <div className="jpd-round-tabs">
                    {rounds.map((r, i) => (
                        <button
                            key={i}
                            className={"jpd-btn" + (activeRound === i ? " jpd-btn-primary" : "")}
                            onClick={() => {
                                setActiveRound(i);
                                setSelected(null);
                            }}
                        >
                            {r.name}
                        </button>
                    ))}
                </div>

                <div
                    className="jpd-builder-categories"
                    style={{ gridTemplateColumns: `repeat(${round.categories.length}, 1fr)` }}
                >
                    {round.categories.map((cat, i) => (
                        <input
                            key={i}
                            className="jpd-input"
                            placeholder={`Category ${i + 1}`}
                            value={cat}
                            onChange={(e) => updateCategory(activeRound, i, e.target.value)}
                        />
                    ))}
                </div>

                <div
                    className="jpd-builder-grid"
                    style={{ gridTemplateColumns: `repeat(${round.categories.length}, 1fr)` }}
                >
                    {round.clues[0].map((_, rowIdx) =>
                        round.categories.map((cat, catIdx) => {
                            const clue = round.clues[catIdx][rowIdx];
                            const filled = clue.type === "image" ? Boolean(clue.prompt) : Boolean(clue.prompt.trim());
                            const isSelected = selected && selected.category === catIdx && selected.row === rowIdx;
                            return (
                                <button
                                    key={`${catIdx}-${rowIdx}`}
                                    className={
                                        "jpd-builder-cell" +
                                        (filled ? " jpd-builder-cell-filled" : "") +
                                        (isSelected ? " jpd-builder-cell-selected" : "")
                                    }
                                    onClick={() => setSelected({ category: catIdx, row: rowIdx })}
                                >
                                    {clue.value}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {selectedClue && (
                <ClueEditor
                    categoryName={round.categories[selected.category] || `Category ${selected.category + 1}`}
                    clue={selectedClue}
                    onChange={(patch) => updateClue(activeRound, selected.category, selected.row, patch)}
                    onImageUpload={(file) => handleImageUpload(activeRound, selected.category, selected.row, file)}
                    onClose={() => setSelected(null)}
                />
            )}

            <div style={{ textAlign: "center" }}>
                <Link className="jpd-btn" to="/jeopardy">
                    Back
                </Link>
            </div>
        </div>
    );
}

function ClueEditor({ categoryName, clue, onChange, onImageUpload, onClose }) {
    return (
        <div className="jpd-panel jpd-panel-wide jpd-clue-editor">
            <div className="jpd-clue-editor-header">
                <strong>
                    {categoryName} — {clue.value}
                </strong>
                <button className="jpd-btn jpd-btn-small" onClick={onClose}>
                    Close
                </button>
            </div>

            <div className="jpd-clue-editor-type">
                <label>
                    <input type="radio" checked={clue.type === "text"} onChange={() => onChange({ type: "text" })} /> Text
                </label>
                <label>
                    <input type="radio" checked={clue.type === "image"} onChange={() => onChange({ type: "image" })} />{" "}
                    Image
                </label>
            </div>

            {clue.type === "text" ? (
                <textarea
                    className="jpd-textarea"
                    placeholder="Question / prompt"
                    value={clue.prompt}
                    onChange={(e) => onChange({ prompt: e.target.value })}
                />
            ) : (
                <div>
                    <input type="file" accept="image/*" onChange={(e) => onImageUpload(e.target.files[0])} />
                    {clue.prompt && <img src={clue.prompt} alt="clue preview" className="jpd-clue-editor-preview" />}
                </div>
            )}

            <input
                className="jpd-input"
                placeholder="Answer"
                value={clue.answer}
                onChange={(e) => onChange({ answer: e.target.value })}
            />
        </div>
    );
}
